/**
 * Date Availability Composable
 *
 * Manages date availability checking for the itinerary date selector.
 * Uses the GET /services/:id/availability endpoint to fetch availability
 * data for a date range and provides O(1) lookup for date validation.
 *
 * Supports progressive month-by-month loading with merge semantics —
 * new availability data is merged into existing data rather than replacing it.
 */
import { ref, computed } from 'vue';
import { calendarRepository } from '@domains/scheduling/repositories/calendar.repository';
export function useDateAvailability() {
    const availabilityData = ref(null);
    const activeLoadCount = ref(0);
    const isLoading = computed(() => activeLoadCount.value > 0);
    const error = ref(null);
    const loadedMonths = ref(new Set());
    const loadingMonths = ref(new Set());
    const monthStatus = ref({});
    const availabilityVersion = ref(0);
    const currentServiceId = ref(null);
    const requestEpoch = ref(0);
    const pendingLoads = new Map();
    const cache = new Map();
    async function loadAvailability(serviceId, startDate, endDate, context) {
        if (endDate < startDate)
            return null;
        const fromStr = formatDateOnly(startDate);
        const toStr = formatDateOnly(endDate);
        const cacheKey = `${serviceId}-${fromStr}-${toStr}`;
        if (cache.has(cacheKey)) {
            const cached = cache.get(cacheKey);
            if (shouldAcceptResponse(serviceId, context?.epoch)) {
                mergeResponse(serviceId, cached, fromStr, toStr);
            }
            return cached;
        }
        activeLoadCount.value++;
        error.value = null;
        try {
            const response = await calendarRepository.getAvailabilityRange(serviceId, fromStr, toStr);
            if (!shouldAcceptResponse(serviceId, context?.epoch)) {
                return null;
            }
            mergeResponse(serviceId, response, fromStr, toStr);
            cache.set(cacheKey, response);
            return response;
        }
        catch (err) {
            if (shouldAcceptResponse(serviceId, context?.epoch)) {
                error.value = 'Error al cargar disponibilidad';
                if (context?.monthKey) {
                    setMonthStatus(context.monthKey, 'error');
                }
            }
            console.error('[useDateAvailability]', { serviceId, range: `${fromStr}..${toStr}`, error: err });
            throw err;
        }
        finally {
            activeLoadCount.value = Math.max(0, activeLoadCount.value - 1);
        }
    }
    function ensureMonthLoaded(serviceId, year, month, options = {}) {
        ensureServiceContext(serviceId);
        const monthKey = toMonthKey(year, month);
        if (isMonthLoaded(year, month))
            return Promise.resolve();
        const pendingKey = `${serviceId}:${monthKey}`;
        const existing = pendingLoads.get(pendingKey);
        if (existing)
            return existing;
        const [startDate, endDate] = clampMonthRange(year, month, options);
        if (!startDate || !endDate || endDate < startDate) {
            setMonthStatus(monthKey, 'loaded');
            loadedMonths.value = new Set([...loadedMonths.value, monthKey]);
            return Promise.resolve();
        }
        const epoch = requestEpoch.value;
        setMonthStatus(monthKey, 'loading');
        loadingMonths.value = new Set([...loadingMonths.value, monthKey]);
        const promise = loadAvailability(serviceId, startDate, endDate, { epoch, monthKey })
            .then((response) => {
            if (!shouldAcceptResponse(serviceId, epoch))
                return;
            if (!response) {
                setMonthStatus(monthKey, 'idle');
                return;
            }
            if (isMonthCoveredByResponse(year, month, response)) {
                setMonthStatus(monthKey, 'loaded');
            }
            else if (!isMonthLoaded(year, month)) {
                setMonthStatus(monthKey, 'idle');
            }
        })
            .catch(() => {
            if (shouldAcceptResponse(serviceId, epoch)) {
                setMonthStatus(monthKey, 'error');
            }
        })
            .finally(() => {
            if (!shouldAcceptResponse(serviceId, epoch)) {
                pendingLoads.delete(pendingKey);
                return;
            }
            const next = new Set(loadingMonths.value);
            next.delete(monthKey);
            loadingMonths.value = next;
            pendingLoads.delete(pendingKey);
        });
        pendingLoads.set(pendingKey, promise);
        return promise;
    }
    function loadMonthAvailability(serviceId, year, month, options = {}) {
        return ensureMonthLoaded(serviceId, year, month, options);
    }
    function getMonthStatus(year, month) {
        return monthStatus.value[toMonthKey(year, month)] ?? 'idle';
    }
    function isMonthLoaded(year, month) {
        return getMonthStatus(year, month) === 'loaded';
    }
    function isMonthLoading(year, month) {
        return getMonthStatus(year, month) === 'loading';
    }
    function isDateLoaded(date) {
        return isMonthLoaded(date.getFullYear(), date.getMonth() + 1);
    }
    function clearAvailability() {
        requestEpoch.value++;
        availabilityData.value = null;
        currentServiceId.value = null;
        activeLoadCount.value = 0;
        loadedMonths.value = new Set();
        loadingMonths.value = new Set();
        monthStatus.value = {};
        pendingLoads.clear();
        cache.clear();
        error.value = null;
    }
    function isDateAvailable(date) {
        if (!availabilityData.value)
            return false;
        const dateStr = formatDateOnly(date);
        return availabilityData.value.dates[dateStr]?.has_availability ?? false;
    }
    function getUnavailableDates() {
        if (!availabilityData.value)
            return [];
        return Object.keys(availabilityData.value.dates).filter(dateStr => !availabilityData.value.dates[dateStr].has_availability);
    }
    function getSlotsForDate(date) {
        if (!availabilityData.value)
            return [];
        const dateStr = formatDateOnly(date);
        return availabilityData.value.dates[dateStr]?.slots ?? [];
    }
    function ensureServiceContext(serviceId) {
        if (currentServiceId.value === serviceId)
            return;
        if (currentServiceId.value !== null && currentServiceId.value !== serviceId) {
            clearAvailability();
        }
        currentServiceId.value = serviceId;
    }
    function shouldAcceptResponse(serviceId, epoch = requestEpoch.value) {
        return requestEpoch.value === epoch && currentServiceId.value === serviceId;
    }
    function mergeResponse(serviceId, response, requestedFromStr, requestedToStr) {
        if (currentServiceId.value !== serviceId)
            return;
        if (availabilityData.value) {
            availabilityData.value = {
                ...availabilityData.value,
                service_id: response.service_id,
                from: minDateStr(availabilityData.value.from, response.from || requestedFromStr),
                to: maxDateStr(availabilityData.value.to, response.to || requestedToStr),
                dates: { ...availabilityData.value.dates, ...response.dates },
            };
        }
        else {
            availabilityData.value = response;
        }
        availabilityVersion.value += 1;
        const responseFrom = response.from || requestedFromStr;
        const responseTo = response.to || requestedToStr;
        addLoadedMonths(responseFrom, responseTo);
    }
    function addLoadedMonths(fromStr, toStr) {
        const [startYear, startMonth] = fromStr.split('-').map(Number);
        const [endYear, endMonth] = toStr.split('-').map(Number);
        if (!Number.isFinite(startYear) || !Number.isFinite(startMonth) ||
            !Number.isFinite(endYear) || !Number.isFinite(endMonth)) {
            return;
        }
        const next = new Set(loadedMonths.value);
        let y = startYear;
        let m = startMonth;
        while (y < endYear || (y === endYear && m <= endMonth)) {
            const monthKey = toMonthKey(y, m);
            next.add(monthKey);
            setMonthStatus(monthKey, 'loaded');
            m++;
            if (m > 12) {
                m = 1;
                y++;
            }
        }
        loadedMonths.value = next;
    }
    function setMonthStatus(monthKey, status) {
        monthStatus.value = {
            ...monthStatus.value,
            [monthKey]: status,
        };
    }
    function clampMonthRange(year, month, options) {
        const firstDay = new Date(year, month - 1, 1);
        const lastDay = new Date(year, month, 0);
        const min = options.minDate ? atStartOfDay(options.minDate) : null;
        const max = options.maxDate ? atStartOfDay(options.maxDate) : null;
        const start = min && firstDay < min ? min : firstDay;
        const end = max && lastDay > max ? max : lastDay;
        if (end < start) {
            return [null, null];
        }
        return [start, end];
    }
    function isMonthCoveredByResponse(year, month, response) {
        const responseFrom = parseDateOnly(response.from);
        const responseTo = parseDateOnly(response.to);
        if (!responseFrom || !responseTo)
            return false;
        const monthStart = new Date(year, month - 1, 1);
        const monthEnd = new Date(year, month, 0);
        return responseFrom <= monthStart && responseTo >= monthEnd;
    }
    return {
        availabilityData,
        availabilityVersion,
        isLoading,
        error,
        loadedMonths,
        loadingMonths,
        monthStatus,
        loadAvailability,
        loadMonthAvailability,
        ensureMonthLoaded,
        getMonthStatus,
        isMonthLoaded,
        isMonthLoading,
        isDateLoaded,
        clearAvailability,
        isDateAvailable,
        getUnavailableDates,
        getSlotsForDate
    };
}
function formatDateOnly(date) {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
}
function parseDateOnly(value) {
    if (!value)
        return null;
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day)
        return null;
    return new Date(year, month - 1, day);
}
function toMonthKey(year, month) {
    return `${year}-${String(month).padStart(2, '0')}`;
}
function atStartOfDay(date) {
    const copy = new Date(date);
    copy.setHours(0, 0, 0, 0);
    return copy;
}
function minDateStr(a, b) {
    return a <= b ? a : b;
}
function maxDateStr(a, b) {
    return a >= b ? a : b;
}
